import { Button, Icon } from "@ttc3k/trekker";
import { format, parse } from "date-fns";
import { EVENT_DATE_FORMAT } from "shared/utils";
import { Box, Flex } from "styled-system/jsx";
import { DateRange, YearGroup } from "widgets/events/types";
import { useEventsWidgetContext } from "../EventsContext";
import { NavArrowRight } from "iconoir-react";
import React from "react";
import { AnimatePresence, motion } from "framer-motion";

type MonthYearSelectorDesktopProps = {
  yearGroups: YearGroup[];
  dateRange: DateRange;
  loading: boolean;
  selectedYear: string;
  selectedMonth: string;
  handleYearClick: (year: string) => void;
  handleMonthClick: (month: string) => void;
};

export const MonthYearSelectorDesktop = ({
  yearGroups,
  dateRange,
  loading,
  selectedYear,
  selectedMonth,
  handleYearClick,
  handleMonthClick,
}: MonthYearSelectorDesktopProps) => {
  const { locale } = useEventsWidgetContext();

  if (yearGroups.length === 0 || !dateRange) {
    return null;
  }

  return (
    <Flex flexDir="row" alignItems={"center"} gap="200" position="relative">
      {yearGroups.map((yearGroup, i) => (
        <React.Fragment key={yearGroup.year}>
          <Flex flexDir="row" alignItems={"center"} gap="6px">
            <Button
              part="date__btn-year"
              visual="unstyled"
              mode="light"
              color="text.dark"
              onClick={() => handleYearClick(yearGroup.year)}
            >
              {yearGroup.year}
            </Button>

            {selectedYear === yearGroup.year && (
              <Icon
                Element={NavArrowRight}
                color="icon.dark"
                w="20px"
                h="20px"
              />
            )}

            <AnimatePresence mode="wait">
              {selectedYear === yearGroup.year && (
                <motion.div
                  variants={{
                    hidden: {
                      opacity: 0,
                      width: 0,
                      transition: {
                        // delay: 0.1,
                        duration: 0.2,
                        ease: "easeInOut",
                      },
                    },
                    visible: {
                      opacity: 1,
                      width: "auto",
                      transition: {
                        duration: 0.2,
                        ease: "easeInOut",
                      },
                    },
                  }}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                >
                  <Flex flexDir="row" alignItems={"center"} gap="11px">
                    {yearGroup.monthsAndEventCounts.map(
                      (monthAndEventCount) => {
                        const isDisabled = monthAndEventCount.eventCount === 0;
                        const isSelected =
                          monthAndEventCount.month === selectedMonth;
                        return (
                          <Button
                            key={monthAndEventCount.month}
                            part="date__btn-month"
                            visual="unstyled"
                            mode="light"
                            color={
                              isSelected && !isDisabled
                                ? "text.accent.blue.mid"
                                : isDisabled
                                  ? "text.mid"
                                  : "text.dark"
                            }
                            disabled={isDisabled}
                            textDecoration={
                              isDisabled ? "line-through" : "none"
                            }
                            onClick={() => {
                              handleMonthClick(monthAndEventCount.month);
                            }}
                          >
                            {format(
                              parse(
                                monthAndEventCount.month,
                                EVENT_DATE_FORMAT,
                                new Date(),
                              ),
                              "MMM",
                              {
                                locale,
                              },
                            )}
                          </Button>
                        );
                      },
                    )}
                  </Flex>
                </motion.div>
              )}
            </AnimatePresence>
          </Flex>
          {i === 0 && <Box bgColor="border.light" w="2px" h="16px" />}
        </React.Fragment>
      ))}
      {loading && (
        <Box position="absolute" top="0" left="0" right="0" bottom="0" />
      )}
    </Flex>
  );
};
